import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
  OktonEmptyState,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link2 } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import { useVincularVarios } from '../../../services/hooks/useConciliadosMutations';
import { useVinculosDaConta } from '../../../services/hooks/useContasBancarias';
import { formatCustomDate } from '../../../utils/formatCustomDate';
import formatValue from '../../../utils/formatValue';

// O que a tela de Conciliação manda junto ao abrir esta. Sem isso não há o que
// baixar: a seleção vive lá, não numa rota que alguém possa digitar.
export interface EstadoBaixarSelecao {
  extrato: string;
  conta: string;
  financeiros: string[];
  valorExtrato: number;
  totalSelecionado: number;
  dataExtrato: string;
  descricaoExtrato: string;
}

/**
 * Confirmação da conciliação de vários títulos em aberto com um lançamento do
 * extrato.
 *
 * Conciliar esses títulos os BAIXA, e a baixa grava Moeda e observação. Antes
 * os dois iam no padrão silencioso — Moeda do próprio banco e a descrição do
 * extrato — e quem concilia só descobria depois, abrindo o título no IFLEX.
 *
 * É tela própria, e não um diálogo sobre a Conciliação: é o padrão do resto do
 * sistema para o que grava.
 */
const BaixarSelecao = () => {
  const history = useHistory();
  const { state } = useLocation<EstadoBaixarSelecao | undefined>();
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const [moeda, setMoeda] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');

  const salvar = useVincularVarios();
  const { data: moedasDaConta = [] } = useVinculosDaConta(state?.conta);

  // Recarregar a página perde a seleção: ela não está na URL, e refazer a
  // conciliação sem saber quais títulos foram escolhidos seria um chute.
  if (!state || state.financeiros.length === 0) {
    return (
      <BankAppPage>
        <OktonPageHeader
          eyebrow="CADASTROS"
          title="Baixar títulos"
          subtitle="Conciliação de vários títulos em aberto"
          onBack={() => history.push('/conciliacao')}
        />
        <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
          <Stack spacing="4" maxW="720px">
            <OktonEmptyState
              title="Seleção indisponível"
              description="A seleção de títulos se perdeu. Volte à Conciliação, escolha o lançamento do extrato e marque os títulos de novo."
            />
            <Flex>
              <Button
                colorScheme="blue"
                onClick={() => history.push('/conciliacao')}
              >
                Voltar para a Conciliação
              </Button>
            </Flex>
          </Stack>
        </Box>
      </BankAppPage>
    );
  }

  const diferenca = Number(
    (state.valorExtrato - state.totalSelecionado).toFixed(2),
  );
  // Sobra só existe quando os títulos ficam abaixo do extrato: aí há o que
  // decidir. Passando dele, o extrato está explicado e fecha.
  const temSobra = diferenca > 0.004;
  const passaDoExtrato = diferenca < -0.004;

  const confirmar = async () => {
    setErro('');
    try {
      await salvar.mutateAsync({
        extrato: state.extrato,
        financeiros: state.financeiros,
        moeda,
        // O Caixa sai da regra do IFLEX, pela data do lançamento do extrato.
        caixa: '',
        observacao,
        sobra: 'conciliado',
      });
      history.push('/conciliacao');
    } catch (err) {
      setErro(
        err instanceof Error ? err.message : 'Erro ao conciliar os títulos.',
      );
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title={`Baixar ${state.financeiros.length} título(s)`}
        subtitle="Conciliação de vários títulos em aberto"
        onBack={() => history.push('/conciliacao')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {!!erro && <OktonAlert status="error" description={erro} />}

          <Box bg={subtleBg} borderRadius="xl" px="4" py="3">
            <Text fontSize="sm" fontWeight="semibold">
              {state.descricaoExtrato}
            </Text>
            <Text fontSize="xs" color="gray.500" mt="1">
              Extrato de{' '}
              {formatCustomDate(
                state.dataExtrato.slice(0, 10),
                'yyyy-MM-dd',
                'dd/MM/yyyy',
              )}{' '}
              — {formatValue(state.valorExtrato)}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {state.financeiros.length} título(s) somando{' '}
              {formatValue(state.totalSelecionado)}
            </Text>
            {/* A diferença fica à vista antes de gravar: é quem concilia que
            diz se aquele lançamento explica aqueles títulos. */}
            {(temSobra || passaDoExtrato) && (
              <Text fontSize="xs" color="orange.500" fontWeight="medium" mt="1">
                Diferença de {formatValue(Math.abs(diferenca))} —{' '}
                {passaDoExtrato
                  ? 'os títulos somam mais que o extrato'
                  : 'os títulos não cobrem o extrato'}
                . Cada um será baixado por inteiro, pelo próprio saldo.
              </Text>
            )}
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="baixarmoeda"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Moeda da baixa
            </Text>
            <OktonSearchSelect
              name="baixarmoeda"
              placeholder="Moeda do banco (padrão)"
              value={moeda}
              onChange={setMoeda}
              options={moedasDaConta
                .filter(m => m.principal_ctm !== '1')
                .map(m => ({
                  value: m.moeda_ctm,
                  label: m.nome_moe,
                }))}
            />
            <Text fontSize="xs" color="gray.500" mt="1">
              Em branco, a baixa usa a Moeda do próprio banco da Conta.
            </Text>
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="baixar-selecao-observacao"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Observação da baixa
            </Text>
            <OktonInput
              name="baixar-selecao-observacao"
              size="lg"
              rounded="2xl"
              fontWeight="light"
              color={color}
              maxLength={1024}
              placeholder={state.descricaoExtrato}
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt="1">
              Em branco, vale a descrição do lançamento do extrato.
            </Text>
          </Box>

          {/* O extrato vale mais que os títulos escolhidos. A diferença não
          vira documento: o lançamento sempre fecha conciliado como está. */}
          {temSobra && (
            <Box borderWidth="1px" rounded="2xl" p="4">
              <Text fontSize="sm" fontWeight="medium">
                Sobra de {formatValue(diferenca)}
              </Text>
              <Text fontSize="xs" color="gray.500" mt="1">
                Os títulos escolhidos não cobrem o lançamento do extrato. A
                diferença não vira documento: o extrato fecha conciliado como
                está.
              </Text>
            </Box>
          )}

          <Text fontSize="xs" color="gray.500">
            O Caixa é o que a regra do IFLEX escolher para a data do lançamento:
            o Caixa aberto do usuário, ou o Caixa Geral.
          </Text>

          <Flex gridGap="3" pt="2">
            <Button
              variant="ghost"
              onClick={() => history.push('/conciliacao')}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                salvar.isLoading ? <Spinner size="sm" /> : <Link2 size={16} />
              }
              isDisabled={salvar.isLoading}
              onClick={confirmar}
            >
              Conciliar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default BaixarSelecao;
